// src/pages/msme/MSMEKanbanHome.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faDownload,
  faTimes,
  faExclamationTriangle,
  faExclamationCircle,
  faCheckCircle,
  faCalendarAlt,
  faComments,
} from "@fortawesome/free-solid-svg-icons";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import jsPDF from 'jspdf';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const dummyTasks = [
  { 
    id: 1, 
    title: "Task 1", 
    description: "Do something important", 
    status: "todo", 
    owner: "John", 
    priority: "high", 
    dueDate: "2025-10-20",
    comments: [
      { id: 1, text: "This needs urgent attention.", timestamp: "2025-10-13T10:00:00" },
      { id: 2, text: "Updated requirements.", timestamp: "2025-10-14T14:30:00" }
    ]
  },
  { 
    id: 2, 
    title: "Task 2", 
    description: "Prepare report", 
    status: "inprogress", 
    owner: "Priya", 
    priority: "medium", 
    dueDate: "2025-10-18",
    comments: [
      { id: 3, text: "Draft is ready for review.", timestamp: "2025-10-12T09:15:00" }
    ]
  },
  { 
    id: 3, 
    title: "Task 3", 
    description: "Submit invoice", 
    status: "done", 
    owner: "Ramesh", 
    priority: "low", 
    dueDate: "2025-10-15",
    comments: []
  },
  { 
    id: 4, 
    title: "Task 4", 
    description: "Team meeting", 
    status: "todo", 
    owner: "Meena", 
    priority: "high", 
    dueDate: "2025-10-16",
    comments: [
      { id: 4, text: "Agenda sent.", timestamp: "2025-10-14T11:00:00" }
    ]
  },
];

const statusColumns = ["todo", "inprogress", "done"];
const statusLabels = { todo: "To Do", inprogress: "In Progress", done: "Done" };
const statusColors = { 
  todo: "bg-indigo-600", 
  inprogress: "bg-amber-500", 
  done: "bg-emerald-600" 
};
const statusHexColors = { todo: "#4f46e5", inprogress: "#d97706", done: "#059669" };

const priorityLevels = ["high", "medium", "low"];
const priorityLabels = { high: "High", medium: "Medium", low: "Low" };
const priorityColors = { 
  high: "bg-red-500 text-white", 
  medium: "bg-amber-500 text-white", 
  low: "bg-emerald-500 text-white" 
};
const priorityIcons = { high: faExclamationTriangle, medium: faExclamationCircle, low: faCheckCircle };
const priorityOrder = { high: 3, medium: 2, low: 1 };

const inputClass = "px-4 py-3 rounded-lg bg-gray-800 text-gray-100 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full text-sm";

const buttonClass = "px-4 py-3 rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-2 min-h-[44px]";

const MSMEKanbanHome = () => {
  const [tasks, setTasks] = useState(dummyTasks);
  const [taskForm, setTaskForm] = useState({ title: "", description: "", status: "todo", owner: "", priority: "medium", dueDate: "" });
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [activeDropZone, setActiveDropZone] = useState(null);

  const resetForm = () => {
    setTaskForm({ title: "", description: "", status: "todo", owner: "", priority: "medium", dueDate: "" });
    setEditingTaskId(null);
    setShowForm(false);
  };

  const resetModal = () => {
    setNewComment("");
    setShowCommentsModal(false);
    setSelectedTask(null);
  };

  const addNotification = (message, type = 'info') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const isOverdue = (dueDate) => dueDate && new Date(dueDate) < new Date();

  const isApproaching = (dueDate) => {
    if (!dueDate) return false;
    const due = new Date(dueDate);
    const now = new Date();
    const diffTime = due - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 3 && diffDays > 0;
  };

  // Drag & Drop handlers
  const onDragStart = (e, taskId) => {
    setDraggedTaskId(taskId);
    e.dataTransfer.setData("taskId", taskId.toString());
  };

  const onDragEnd = (e) => {
    setDraggedTaskId(null);
    setActiveDropZone(null);
  };

  const onDragEnter = (e, status) => {
    if (draggedTaskId) {
      setActiveDropZone(status);
    }
  };

  const onDragLeave = (e) => {
    setActiveDropZone(null);
  };

  const onDrop = (e, newStatus) => {
    const taskId = parseInt(e.dataTransfer.getData("taskId"));
    setTasks(tasks.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    addNotification(`Task moved to ${statusLabels[newStatus]}`, 'info');
    setDraggedTaskId(null);
    setActiveDropZone(null);
  };

  const allowDrop = (e) => e.preventDefault();

  // Add / Edit Task
  const handleSubmit = (e) => {
    e.preventDefault();
    if (taskForm.title.trim() === "" || taskForm.description.trim() === "" || taskForm.owner.trim() === "") {
      addNotification("Please fill in all required fields.", 'error');
      return;
    }
    if (editingTaskId) {
      setTasks(tasks.map(t => t.id === editingTaskId ? { ...t, ...taskForm } : t));
      addNotification("Task updated!", 'success');
    } else {
      const newTask = { 
        id: Date.now(), 
        ...taskForm, 
        comments: [] 
      };
      setTasks([...tasks, newTask]);
      addNotification(`Task added and assigned to ${taskForm.owner}!`, 'success');
    }
    resetForm();
  };

  const handleEdit = (task) => {
    setTaskForm({ title: task.title, description: task.description, status: task.status, owner: task.owner, priority: task.priority, dueDate: task.dueDate });
    setEditingTaskId(task.id);
    setShowForm(true);
  };

  const handleDelete = (taskId) => {
    if (window.confirm("Are you sure you want to delete this task?")) {
      setTasks(tasks.filter(t => t.id !== taskId));
      addNotification("Task deleted!", 'error');
    }
  };

  const handleAddComment = (e) => {
    e.preventDefault();
    if (newComment.trim() === "") return;
    const comment = {
      id: Date.now(),
      text: newComment.trim(),
      timestamp: new Date().toISOString()
    };
    setTasks(tasks.map(t => t.id === selectedTask.id ? { ...t, comments: [...t.comments, comment] } : t));
    setNewComment("");
    addNotification("Comment added!", 'success');
  };

  const handlePDFExport = () => {
    const doc = new jsPDF();
    doc.text("MSME Kanban Board", 10, 10);
    let y = 20;
    statusColumns.forEach(status => {
      doc.text(`${statusLabels[status]}:`, 10, y);
      const filteredTasks = tasks.filter(t => t.status === status).sort((a, b) => priorityOrder[b.priority] - priorityOrder[a.priority]);
      filteredTasks.forEach(t => {
        y += 10;
        doc.text(`- ${t.title} [Owner: ${t.owner}, Priority: ${priorityLabels[t.priority]}, Due: ${t.dueDate || 'No due date'}]`, 15, y);
        t.comments.forEach(c => {
          y += 8;
          doc.text(`  Comment: ${c.text}`, 20, y);
        });
      });
      y += 10;
    });
    doc.save("kanban_board.pdf");
    addNotification("PDF Exported!", 'success');
  };

  // Sort tasks by priority (high to low)
  const sortTasksByPriority = (tasksList) => {
    return [...tasksList].sort((a, b) => priorityOrder[b.priority] - priorityOrder[a.priority]);
  };

  // Metrics Chart
  const chartData = {
    labels: statusColumns.map(s => statusLabels[s]),
    datasets: [{
      label: 'Tasks Count',
      data: statusColumns.map(s => tasks.filter(t => t.status === s).length),
      backgroundColor: statusColumns.map(s => statusHexColors[s])
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { 
      legend: { display: false },
      title: { display: true, text: 'Task Distribution by Status', font: { size: 16 } }
    },
    scales: {
      y: { beginAtZero: true, ticks: { font: { size: 12 } } }
    }
  };

  if (showCommentsModal && selectedTask) {
    const taskComments = selectedTask.comments.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    return (
      <div className="min-h-screen bg-gray-900 text-gray-100 p-4 fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={resetModal}></div>
        {/* Modal */}
        <div className="relative bg-gray-800 p-6 rounded-xl max-w-md mx-auto mt-12 shadow-2xl">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold">Comments for: {selectedTask.title}</h3>
            <button 
              onClick={resetModal}
              className="p-2 text-gray-400 hover:text-gray-200 text-xl"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
            {taskComments.length === 0 ? (
              <p className="text-gray-500 text-center italic text-base">No comments yet.</p>
            ) : (
              taskComments.map(comment => (
                <div key={comment.id} className="bg-gray-700 p-4 rounded-lg">
                  <p className="text-base text-gray-300 mb-2">{comment.text}</p>
                  <p className="text-sm text-gray-500">{new Date(comment.timestamp).toLocaleString()}</p>
                </div>
              ))
            )}
          </div>
          <form onSubmit={handleAddComment} className="space-y-3">
            <textarea
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              rows={3}
              className={`${inputClass} resize-none`}
            />
            <button 
              type="submit" 
              className={`${buttonClass} w-full bg-indigo-600 hover:bg-indigo-700 text-white`}
              disabled={!newComment.trim()}
            >
              Add Comment
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4">
      {/* Header */}
      <header className="flex flex-row justify-between items-center gap-4 mb-6">
        <h1 className="text-xl font-bold text-white">MSME Kanban Board</h1>
      </header>

      <div className="flex flex-row gap-3">
        {!showForm && (
          <button 
            onClick={() => setShowForm(true)} 
            className={`${buttonClass} bg-emerald-600 hover:bg-emerald-700 text-white`}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Task
          </button>
        )}
        <button 
          onClick={handlePDFExport} 
          className={`${buttonClass} bg-indigo-600 hover:bg-indigo-700 text-white`}
        >
          <FontAwesomeIcon icon={faDownload} /> Export PDF
        </button>
      </div>

      {/* Add / Edit Task Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-gray-800 p-6 rounded-xl mb-6 shadow-xl">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-white">{editingTaskId ? "Edit Task" : "Add New Task"}</h2>
            <button 
              type="button" 
              onClick={resetForm}
              className="p-2 text-gray-400 hover:text-gray-200 text-lg"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <input
                required
                type="text"
                placeholder="Task Title"
                value={taskForm.title}
                onChange={e => setTaskForm({ ...taskForm, title: e.target.value })}
                className={inputClass}
              />
              <input
                required
                type="text"
                placeholder="Owner"
                value={taskForm.owner}
                onChange={e => setTaskForm({ ...taskForm, owner: e.target.value })}
                className={inputClass}
              />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <select
                required
                value={taskForm.status}
                onChange={e => setTaskForm({ ...taskForm, status: e.target.value })}
                className={inputClass}
              >
                {statusColumns.map(s => (
                  <option key={s} value={s}>{statusLabels[s]}</option>
                ))}
              </select>
              <select
                required
                value={taskForm.priority}
                onChange={e => setTaskForm({ ...taskForm, priority: e.target.value })}
                className={inputClass}
              >
                {priorityLevels.map(p => (
                  <option key={p} value={p}>{priorityLabels[p]}</option>
                ))}
              </select>
              <input
                type="date"
                placeholder="Due Date"
                value={taskForm.dueDate}
                onChange={e => setTaskForm({ ...taskForm, dueDate: e.target.value })}
                className={inputClass}
              />
            </div>
            <textarea
              required
              placeholder="Task Description"
              value={taskForm.description}
              onChange={e => setTaskForm({ ...taskForm, description: e.target.value })}
              rows={4}
              className={`${inputClass} resize-none col-span-1 lg:col-span-2`}
            />
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button 
                type="submit" 
                className={`${buttonClass} bg-emerald-600 hover:bg-emerald-700 text-white flex-1`}
              >
                {editingTaskId ? "Update Task" : "Add Task"}
              </button>
              {editingTaskId && (
                <button 
                  type="button"
                  onClick={resetForm}
                  className={`${buttonClass} bg-gray-600 hover:bg-gray-700 text-white flex-1`}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </form>
      )}

      {/* Kanban Board */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8 overflow-x-auto">
        {statusColumns.map(status => {
          const filteredTasks = sortTasksByPriority(tasks.filter(t => t.status === status));
          return (
            <section
              key={status}
              className={`bg-gray-800 p-6 rounded-xl shadow-lg transition-all duration-300 ${
                activeDropZone === status 
                  ? 'ring-2 ring-indigo-500 bg-indigo-900/10 scale-105' 
                  : ''
              }`}
              onDrop={e => onDrop(e, status)}
              onDragOver={allowDrop}
              onDragEnter={e => onDragEnter(e, status)}
              onDragLeave={onDragLeave}
            >
              <h3 className={`font-semibold mb-6 p-3 rounded-lg text-white text-center text-base ${statusColors[status]}`}>
                {statusLabels[status]} ({filteredTasks.length})
              </h3>
              <div className="space-y-4">
                {filteredTasks.map(task => {
                  const overdue = isOverdue(task.dueDate);
                  const approaching = isApproaching(task.dueDate);
                  const dueClass = overdue ? "text-red-400" : approaching ? "text-amber-400" : "text-gray-400";
                  return (
                    <article
                      key={task.id}
                      draggable
                      onDragStart={e => onDragStart(e, task.id)}
                      onDragEnd={onDragEnd}
                      className={`bg-gray-700 p-4 rounded-lg cursor-move shadow-md hover:shadow-xl transition-all duration-300 ${
                        task.id === draggedTaskId ? 'opacity-50 rotate-3' : ''
                      } ${overdue ? 'border-l-4 border-red-500' : ''}`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-start justify-between mb-4 gap-4">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-base text-white mb-2 break-words">{task.title}</h4>
                          <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${priorityColors[task.priority]}`}>
                            <FontAwesomeIcon icon={priorityIcons[task.priority]} className="w-4 h-4" />
                            {priorityLabels[task.priority]}
                          </span>
                        </div>
                        <div className="flex gap-2 flex-shrink-0 justify-end">
                          <button 
                            onClick={() => { setSelectedTask(task); setShowCommentsModal(true); }} 
                            className={`${buttonClass} bg-indigo-600 hover:bg-indigo-700 text-white min-w-[44px]`}
                            title="Comments"
                            aria-label="View comments"
                          >
                            <FontAwesomeIcon icon={faComments} />
                            {task.comments.length > 0 && (
                              <span className="absolute -top-1 -right-1 bg-red-500 text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                {task.comments.length}
                              </span>
                            )}
                          </button>
                          <button 
                            onClick={() => handleEdit(task)} 
                            className={`${buttonClass} bg-amber-500 hover:bg-amber-600 text-white min-w-[44px]`}
                            title="Edit"
                            aria-label="Edit task"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button 
                            onClick={() => handleDelete(task.id)} 
                            className={`${buttonClass} bg-red-500 hover:bg-red-600 text-white min-w-[44px]`}
                            title="Delete"
                            aria-label="Delete task"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                      <p className="text-base text-gray-300 mb-4 leading-relaxed break-words">{task.description}</p>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <span className={`flex items-center gap-2 ${dueClass}`}>
                          <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4" />
                          {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'No due date'}
                          {overdue && <span className="text-red-400 font-medium">(Overdue!)</span>}
                          {approaching && <span className="text-amber-400 font-medium">(Approaching)</span>}
                        </span>
                        <span className="text-gray-400">Owner: {task.owner}</span>
                      </div>
                    </article>
                  );
                })}
              </div>
              {filteredTasks.length === 0 && (
                <p className="text-gray-500 text-center py-8 italic text-base">No tasks here. Drag one over!</p>
              )}
            </section>
          );
        })}
      </div>

      {/* Metrics Chart */}
      <div className="bg-gray-800 p-6 rounded-xl shadow-xl">
        <h3 className="font-semibold mb-6 text-center text-xl text-white">Board Overview</h3>
        <div className="h-80">
          <Bar data={chartData} options={chartOptions} />
        </div>
      </div>

      {/* Notifications */}
      {notifications.map(notif => (
        <div 
          key={notif.id} 
          className={`fixed top-4 right-4 p-4 rounded-xl shadow-2xl z-50 text-white max-w-sm ${
            notif.type === 'success' ? 'bg-emerald-600' : 
            notif.type === 'error' ? 'bg-red-500' : 'bg-indigo-600'
          } animate-slide-in-right`}
          style={{ animation: 'slideInRight 0.3s ease-out' }}
        >
          <div className="flex justify-between items-start">
            <span className="flex-1 text-base">{notif.message}</span>
            <button 
              onClick={() => removeNotification(notif.id)} 
              className="ml-4 text-white hover:text-gray-200 text-xl"
            >
              ×
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default MSMEKanbanHome;